import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/tr';
import './Patients.css'; // Tasarım bütünlüğü için aynı CSS'i kullanıyoruz
import { toast } from 'react-toastify';

moment.locale('tr');

function Home({ user, openTab }) {
    const [stats, setStats] = useState({
        totalPatients: 0,
        todayAppointments: 0,
        upcomingAppointments: [],
        earnings: [], // Kazanç verisi
        birthdays: [], // Doğum günü olanlar
        lowStockItems: [], // Kritik stok
        topMedicines: [], // En çok kullanılan ilaçlar
        superAdminStats: null, // Süper Admin verileri
        systemStatus: null, // Sistem Durumu verileri
        announcements: [] // Duyurular
    });
    const [selectedAnnouncements, setSelectedAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Süper Admin ise özel istatistikleri çek
        if (user.role === 'super_admin') {
            const fetchData = async () => {
                try {
                    const resStats = await axios.get('./backend/get_super_admin_stats.php');
                    setStats(prev => ({ ...prev, superAdminStats: resStats.data }));
                } catch (e) { console.error("Stats Error:", e); }

                try {
                    const resSys = await axios.get('./backend/get_system_status.php');
                    setStats(prev => ({ ...prev, systemStatus: resSys.data }));
                } catch (e) { console.error("Sys Status Error:", e); }

                try {
                    const resAnn = await axios.get('./backend/get_all_announcements.php');
                    setStats(prev => ({ ...prev, announcements: Array.isArray(resAnn.data) ? resAnn.data : [] }));
                } catch (e) { console.error("Announcements Error:", e); }

                setLoading(false);
            };
            fetchData();
            return;
        }

        if (user && ['doctor', 'admin', 'staff'].includes(user.role)) {
            const fetchData = async () => {
                try {
                    // Doktor ise sadece kendi hastalarını çek, değilse hepsini
                    let patientsUrl = './backend/get_patients.php';
                    if (user.role === 'doctor') {
                        patientsUrl += `?doctor_id=${user.id}`;
                    }

                    // Paralel olarak hasta ve randevu verilerini çek
                    const requests = [
                        axios.get(patientsUrl),
                        axios.get(`./backend/get_appointments.php?role=${user.role}`),
                        axios.get('./backend/get_inventory.php'),
                        axios.get('./backend/get_top_medicines.php')
                    ];

                    // Personel kazanç verilerini görmemeli
                    if (user.role === 'admin' || user.role === 'doctor') {
                        requests.push(axios.get('./backend/get_earnings.php'));
                    }

                    const responses = await Promise.all(requests);

                    const patients = Array.isArray(responses[0].data) ? responses[0].data : [];
                    const appointments = Array.isArray(responses[1].data) ? responses[1].data : [];
                    const inventory = Array.isArray(responses[2].data) ? responses[2].data : [];
                    const topMedicines = Array.isArray(responses[3].data) ? responses[3].data : [];
                    
                    // Kazanç verisi sadece admin/doktor için
                    const earningsRaw = (user.role === 'admin' || user.role === 'doctor') && responses[4] 
                        ? (Array.isArray(responses[4].data) ? responses[4].data : []) 
                        : [];

                    // Bu yılın tüm aylarını içeren bir yapı oluştur
                    const allMonths = moment.months().map(monthName => ({
                        month: monthName.charAt(0).toUpperCase() + monthName.slice(1), // İlk harfi büyük yap
                        amount: 0,
                    }));

                    // Gelen veriyi bu yapıya işle (Backend'den gelen ay adının Türkçe ve küçük harf olduğunu varsayıyoruz)
                    const monthNamesLower = moment.months().map(m => m.toLowerCase());
                    earningsRaw.forEach(earnedMonth => {
                        const monthIndex = monthNamesLower.indexOf(earnedMonth.month.toLowerCase());
                        if (monthIndex !== -1) {
                            allMonths[monthIndex].amount = earnedMonth.amount;
                        }
                    });

                    // Bugünün tarihini al (YYYY-MM-DD formatında)
                    const today = new Date().toISOString().split('T')[0];
                    const todayApps = appointments.filter(app => app.appointment_date.startsWith(today));

                    // Gelecek randevuları filtrele ve tarihe göre sırala
                    const now = new Date();
                    const upcoming = appointments
                        .filter(app => new Date(app.appointment_date) > now)
                        .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))
                        .slice(0, 5); // Sadece ilk 5 tanesini al

                    // Bugün doğum günü olanları bul
                    const todayDate = new Date();
                    const birthdaysToday = patients.filter(p => {
                        if (!p.birth_date) return false;
                        const dob = new Date(p.birth_date);
                        return dob.getDate() === todayDate.getDate() && dob.getMonth() === todayDate.getMonth();
                    });

                    // Kritik stok kontrolü
                    const lowStock = inventory.filter(item => parseInt(item.quantity) <= parseInt(item.min_level));

                    setStats({
                        totalPatients: patients.length,
                        todayAppointments: todayApps.length,
                        upcomingAppointments: upcoming,
                        earnings: allMonths,
                        birthdays: birthdaysToday,
                        lowStockItems: lowStock,
                        topMedicines: topMedicines
                    });
                    setLoading(false);
                } catch (error) {
                    console.error("Dashboard veri hatası:", error);
                    setLoading(false);
                }
            };
            fetchData();
        } else {
            setLoading(false);
        }
    }, [user]);

    // Grafik için maksimum değeri bul (yükseklik hesaplaması için)
    const maxEarning = stats.earnings.length > 0 ? Math.max(...stats.earnings.map(e => parseFloat(e.amount))) : 0;

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Byte';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const handleDownloadBackup = () => {
        window.open('./backend/download_full_backup.php', '_blank');
    };

    const handleSelectAnnouncement = (id) => {
        if (selectedAnnouncements.includes(id)) {
            setSelectedAnnouncements(selectedAnnouncements.filter(item => item !== id));
        } else {
            setSelectedAnnouncements([...selectedAnnouncements, id]);
        }
    };

    const handleSelectAllAnnouncements = (e) => {
        if (e.target.checked) {
            setSelectedAnnouncements(stats.announcements.map(a => a.id));
        } else {
            setSelectedAnnouncements([]);
        }
    };

    const handleBulkDeleteAnnouncements = () => {
        if (selectedAnnouncements.length === 0) return;
        if (window.confirm(`${selectedAnnouncements.length} adet duyuruyu silmek istediğinize emin misiniz?`)) {
            axios.post('./backend/delete_announcements_bulk.php', { ids: selectedAnnouncements })
                .then(res => {
                    toast.success(res.data.message);
                    setStats(prev => ({
                        ...prev,
                        announcements: prev.announcements.filter(a => !selectedAnnouncements.includes(a.id))
                    }));
                    setSelectedAnnouncements([]);
                })
                .catch(err => toast.error("Silme işlemi başarısız."));
        }
    };

    const handleDeleteAnnouncement = (id) => {
        if (window.confirm("Bu duyuruyu silmek istediğinize emin misiniz?")) {
            axios.post('./backend/delete_announcement.php', { id })
                .then(res => {
                    toast.success(res.data.message);
                    setStats(prev => ({
                        ...prev,
                        announcements: prev.announcements.filter(a => a.id !== id)
                    }));
                    setSelectedAnnouncements(prev => prev.filter(itemId => itemId !== id));
                })
                .catch(err => toast.error("Silme işlemi başarısız."));
        }
    };

    if (loading) return <div>Yükleniyor...</div>;

    // Süper Admin Dashboard Tasarımı
    if (user.role === 'super_admin') {
        const saStats = stats.superAdminStats || {};
        const sysStats = stats.systemStatus || {};
        return (
            <div className="patients-container">
                <h2>Süper Admin Paneli</h2>
                
                {/* İstatistik Kartları */}
                <div className="responsive-flex-container">
                    <div className="responsive-flex-item" style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', borderLeft: '5px solid #4e73df' }}>
                        <h3 style={{ margin: 0, color: '#5a5c69', fontSize: '0.9rem', textTransform: 'uppercase' }}>Toplam Klinik</h3>
                        <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#5a5c69', margin: '10px 0 0 0' }}>{saStats.total_clinics || 0}</p>
                    </div>
                    <div className="responsive-flex-item" style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', borderLeft: '5px solid #1cc88a' }}>
                        <h3 style={{ margin: 0, color: '#5a5c69', fontSize: '0.9rem', textTransform: 'uppercase' }}>Aktif Klinik</h3>
                        <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#5a5c69', margin: '10px 0 0 0' }}>{saStats.active_clinics || 0}</p>
                    </div>
                    <div className="responsive-flex-item" style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', borderLeft: '5px solid #f6c23e' }}>
                        <h3 style={{ margin: 0, color: '#5a5c69', fontSize: '0.9rem', textTransform: 'uppercase' }}>Tahmini Aylık Gelir</h3>
                        <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#5a5c69', margin: '10px 0 0 0' }}>{parseFloat(saStats.mrr || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</p>
                    </div>
                </div>

                <div className="responsive-flex-container">
                    {/* Hızlı Erişim */}
                    <div style={{ flex: 1, padding: '30px', background: 'white', borderRadius: '15px', boxShadow: '0 5px 20px rgba(0,0,0,0.05)', textAlign: 'center' }}>
                        <h3 style={{ color: '#4e73df', marginBottom: '15px' }}>Sistem Yönetimi</h3>
                        <p style={{ color: '#5a5c69', marginBottom: '25px' }}>Sistemdeki klinikleri yönetmek, yeni klinik oluşturmak ve durumlarını değiştirmek için klinik yönetimi sayfasına gidin.</p>
                        <button onClick={() => openTab('clinics')} className="add-btn" style={{ margin: '0 auto', fontSize: '1.1rem', padding: '15px 30px' }}>🏥 Klinik Yönetimine Git</button>
                    </div>

                    {/* Paket Dağılımı */}
                    <div style={{ flex: 1, padding: '30px', background: 'white', borderRadius: '15px', boxShadow: '0 5px 20px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ color: '#4e73df', marginBottom: '20px', marginTop: 0 }}>Aktif Paket Dağılımı</h3>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {saStats.distribution && saStats.distribution.map((d, i) => (
                                <li key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: d.color || '#ccc', marginRight: '10px' }}></span>
                                        {d.name}
                                    </span>
                                    <strong>{d.count} Klinik</strong>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Sistem Durumu */}
                <div style={{ marginTop: '30px', background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>
                        <h3 style={{ color: '#4e73df', margin: 0 }}>🖥️ Sistem Durumu</h3>
                        <button onClick={handleDownloadBackup} className="add-btn" style={{ fontSize: '0.9rem', padding: '8px 15px', background: '#36b9cc' }}>
                            📥 Veritabanı Yedeği İndir
                        </button>
                    </div>
                    
                    <div className="responsive-grid-2" style={{ marginBottom: '30px' }}>
                        <div className="detail-row">
                            <span className="detail-label">PHP Sürümü:</span>
                            <span className="detail-value">{sysStats.php_version}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">MySQL Sürümü:</span>
                            <span className="detail-value">{sysStats.mysql_version}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Toplam DB Boyutu:</span>
                            <span className="detail-value" style={{fontWeight: 'bold', color: '#1cc88a'}}>{formatBytes(sysStats.total_size)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Son Migrasyon:</span>
                            <span className="detail-value">{sysStats.last_migration ? new Date(sysStats.last_migration).toLocaleString() : '-'}</span>
                        </div>
                    </div>

                    <h4 style={{ color: '#5a5c69', marginBottom: '15px' }}>Veritabanı Tabloları</h4>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ fontSize: '0.9rem' }}>
                            <thead>
                                <tr>
                                    <th>Tablo Adı</th>
                                    <th>Motor</th>
                                    <th>Satır Sayısı</th>
                                    <th>Boyut</th>
                                    <th>Son Güncelleme</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sysStats.tables && sysStats.tables.map(t => (
                                    <tr key={t.name}>
                                        <td>{t.name}</td>
                                        <td>{t.engine}</td>
                                        <td>{t.rows}</td>
                                        <td>{formatBytes(t.size)}</td>
                                        <td>{t.updated ? new Date(t.updated).toLocaleString() : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Duyuru Yönetimi */}
                <div style={{ marginTop: '30px', background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>
                        <h3 style={{ color: '#4e73df', margin: 0 }}>📢 Duyuru Yönetimi</h3>
                        <button 
                            onClick={handleBulkDeleteAnnouncements} 
                            className="delete-btn" 
                            style={{ 
                                fontSize: '0.9rem', 
                                padding: '8px 15px', 
                                opacity: selectedAnnouncements.length > 0 ? 1 : 0.5, 
                                cursor: selectedAnnouncements.length > 0 ? 'pointer' : 'not-allowed' 
                            }}
                            disabled={selectedAnnouncements.length === 0}
                        >
                            Seçilenleri Sil ({selectedAnnouncements.length})
                        </button>
                    </div>
                    
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ fontSize: '0.9rem' }}>
                            <thead>
                                <tr>
                                    <th style={{width: '40px'}}><input type="checkbox" onChange={handleSelectAllAnnouncements} checked={stats.announcements.length > 0 && selectedAnnouncements.length === stats.announcements.length} /></th>
                                    <th>Başlık</th>
                                    <th>Mesaj</th>
                                    <th>Tip</th>
                                    <th>Tarih</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.announcements && stats.announcements.map(ann => (
                                    <tr key={ann.id}>
                                        <td><input type="checkbox" checked={selectedAnnouncements.includes(ann.id)} onChange={() => handleSelectAnnouncement(ann.id)} /></td>
                                        <td>{ann.title}</td>
                                        <td>{ann.message.replace(/<[^>]+>/g, '').substring(0, 50)}{ann.message.replace(/<[^>]+>/g, '').length > 50 ? '...' : ''}</td>
                                        <td>
                                            <span className={`status-badge status-${ann.type === 'danger' ? 'cancelled' : (ann.type === 'success' ? 'completed' : 'scheduled')}`}>
                                                {ann.type}
                                            </span>
                                        </td>
                                        <td>{new Date(ann.created_at).toLocaleDateString()}</td>
                                        <td>
                                            <button className="delete-btn" onClick={() => handleDeleteAnnouncement(ann.id)} style={{padding: '5px 10px', fontSize: '0.8rem'}}>Sil</button>
                                        </td>
                                    </tr>
                                ))}
                                {(!stats.announcements || stats.announcements.length === 0) && (
                                    <tr><td colSpan="6" style={{textAlign: 'center'}}>Duyuru bulunamadı.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // Hasta girişi için basit karşılama
    if (user.role === 'patient') {
        return (
            <div className="patients-container">
                <div style={{ textAlign: 'center', padding: '40px 20px', background: 'white', borderRadius: '15px', boxShadow: '0 5px 20px rgba(0,0,0,0.05)' }}>
                    <h2 style={{ color: '#4e73df', marginBottom: '10px' }}>Hoşgeldiniz, {user.full_name}</h2>
                    <p style={{ color: '#5a5c69', marginBottom: '30px' }}>Diş kliniği randevu yönetim sistemine hoş geldiniz. Aşağıdaki butonu kullanarak randevularınızı görüntüleyebilir ve yeni randevu oluşturabilirsiniz.</p>
                    
                    <button onClick={() => openTab('appointments')} className="add-btn" style={{ margin: '0 auto', fontSize: '1.1rem', padding: '15px 30px' }}>📅 Randevularıma Git</button>
                </div>
            </div>
        )
    }

    // Doktor Dashboard Tasarımı
    return (
        <div className="patients-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Genel Bakış</h2>
                <a 
                    href="https://wa.me/905336136464" 
                    target="_blank" 
                    rel="noreferrer" 
                    className="add-btn" 
                    style={{ 
                        background: '#25D366', 
                        color: 'white', 
                        textDecoration: 'none', 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        padding: '10px 20px', 
                        borderRadius: '50px', 
                        boxShadow: '0 4px 15px rgba(37, 211, 102, 0.4)', 
                        border: 'none', 
                        fontSize: '0.95rem', 
                        fontWeight: '600' 
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.536 0 1.52 1.115 2.988 1.264 3.186.149.198 2.19 3.361 5.27 4.693 2.151.928 2.985.94 3.553.874.618-.071 1.758-.718 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.815 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                    Yardım
                </a>
            </div>

            {/* Hızlı İşlemler Menüsü */}
            <div className="responsive-flex-container" style={{ gap: '15px' }}>
                <button onClick={() => openTab('appointments')} className="add-btn" style={{ background: 'white', color: '#4e73df', border: '1px solid #4e73df', boxShadow: 'none' }}>
                    <img src="/uploads/iconpack/calendar_week1.svg" alt="Randevu" style={{ width: '18px', height: '18px' }} />
                    Yeni Randevu
                </button>
                <button onClick={() => openTab('patients')} className="add-btn" style={{ background: 'white', color: '#1cc88a', border: '1px solid #1cc88a', boxShadow: 'none' }}>
                    <img src="/uploads/iconpack/sick.svg" alt="Hasta" style={{ width: '18px', height: '18px' }} />
                    Yeni Hasta
                </button>
                {(user.role === 'admin' || user.role === 'doctor') && (
                    <>
                        <button onClick={() => openTab('payments')} className="add-btn" style={{ background: 'white', color: '#f6c23e', border: '1px solid #f6c23e', boxShadow: 'none' }}>
                            <img src="/uploads/iconpack/mastercard_credit_card.svg" alt="Ödeme" style={{ width: '18px', height: '18px' }} />
                            Ödeme Al
                        </button>
                        <button onClick={() => openTab('lab-orders')} className="add-btn" style={{ background: 'white', color: '#36b9cc', border: '1px solid #36b9cc', boxShadow: 'none' }}>
                            <img src="/uploads/iconpack/clipboard.svg" alt="İş Emri" style={{ width: '18px', height: '18px' }} />
                            İş Emri
                        </button>
                    </>
                )}
            </div>

            {/* Stok Uyarıları */}
            {stats.lowStockItems.length > 0 && user.role !== 'staff' && (
                <div style={{ background: '#ffebee', padding: '15px', borderRadius: '10px', border: '1px solid #ffcdd2', marginBottom: '30px', color: '#c62828' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src="/uploads/iconpack/error.svg" alt="Uyarı" style={{ width: '20px', height: '20px' }} /> Kritik Stok Uyarısı ({stats.lowStockItems.length} Ürün)
                    </h3>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem' }}>
                        {stats.lowStockItems.slice(0, 5).map(item => (
                            <li key={item.id}>
                                <strong>{item.item_name}</strong>: {item.quantity} {item.unit} (Min: {item.min_level})
                            </li>
                        ))}
                        {stats.lowStockItems.length > 5 && <li>Ve {stats.lowStockItems.length - 5} ürün daha...</li>}
                    </ul>
                    <div style={{ marginTop: '10px' }}>
                        <span 
                            onClick={() => openTab('inventory', { filterLowStock: true })} 
                            style={{ color: '#c62828', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}>
                            Stok Yönetimine Git &rarr;
                        </span>
                    </div>
                </div>
            )}

            {/* İstatistik Kartları */}
            <div className="responsive-flex-container">
                <div className="responsive-flex-item" onClick={() => openTab('patients')} style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', borderLeft: '5px solid #4e73df', cursor: 'pointer', transition: 'transform 0.2s' }} onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                    <h3 style={{ margin: 0, color: '#5a5c69', fontSize: '0.9rem', textTransform: 'uppercase' }}>{user.role === 'doctor' ? 'Hastalarım' : 'Toplam Hasta'}</h3>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#5a5c69', margin: '10px 0 0 0' }}>{stats.totalPatients}</p>
                </div>
                <div className="responsive-flex-item" onClick={() => openTab('appointments')} style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', borderLeft: '5px solid #1cc88a', cursor: 'pointer', transition: 'transform 0.2s' }} onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                    <h3 style={{ margin: 0, color: '#5a5c69', fontSize: '0.9rem', textTransform: 'uppercase' }}>Bugünkü Randevular</h3>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#5a5c69', margin: '10px 0 0 0' }}>{stats.todayAppointments}</p>
                </div>
                <div className="responsive-flex-item" style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', borderLeft: '5px solid #f6c23e' }}>
                    <h3 style={{ margin: 0, color: '#5a5c69', fontSize: '0.9rem', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src="/uploads/iconpack/confetti.svg" alt="Doğum Günü" style={{ width: '20px', height: '20px' }} /> Bugün Doğum Günleri
                    </h3>
                    {stats.birthdays.length > 0 ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0 0', fontSize: '0.9rem', color: '#5a5c69' }}>
                            {stats.birthdays.map(p => (
                                <li key={p.id} style={{ marginBottom: '5px' }}>
                                    🎉 <strong>{p.full_name}</strong>
                                    <span style={{ fontSize: '0.8rem', color: '#858796', marginLeft: '5px' }}>
                                        ({new Date().getFullYear() - new Date(p.birth_date).getFullYear()} yaş)
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p style={{ fontSize: '0.9rem', color: '#858796', margin: '10px 0 0 0' }}>Bugün doğum günü olan hasta yok.</p>
                    )}
                </div>
            </div>

            {/* Aylık Kazanç Grafiği */}
            {(user.role === 'admin' || user.role === 'doctor') && (
            <div style={{ background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#4e73df' }}>Aylık Kazanç Grafiği (Bu Yıl)</h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', height: '200px', gap: '10px', paddingBottom: '20px', borderBottom: '1px solid #eee', overflowX: 'auto' }}>
                    {stats.earnings.map((item, index) => {
                        const heightPercent = maxEarning > 0 ? (item.amount / maxEarning) * 100 : 0;
                        return (
                            <div key={index} className="chart-bar-group" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                                <div className="chart-tooltip">
                                    {parseFloat(item.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                </div>
                                <div 
                                    className="chart-bar"
                                    style={{ 
                                        width: '100%', 
                                        height: `${heightPercent}%`, 
                                        background: '#4e73df', 
                                        borderRadius: '5px 5px 0 0',
                                        transition: 'all 0.3s ease',
                                        minHeight: item.amount > 0 ? '4px' : '0'
                                    }} 
                                ></div>
                                <span style={{ fontSize: '0.75rem', color: '#858796', marginTop: '5px', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{item.month}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
            )}

            {/* En Çok Kullanılan İlaçlar */}
            <div style={{ background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#4e73df', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src="/uploads/iconpack/pill.svg" alt="İlaç" style={{ width: '24px', height: '24px' }} /> En Çok Yazılan İlaçlar
                </h3>
                {stats.topMedicines.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'wrap', gap: '20px' }}>
                        {/* Pasta Grafik */}
                        <div style={{
                            width: '200px',
                            height: '200px',
                            borderRadius: '50%',
                            background: (() => {
                                const total = stats.topMedicines.reduce((acc, curr) => acc + curr.count, 0);
                                let currentPercent = 0;
                                const colors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'];
                                const gradient = stats.topMedicines.map((item, index) => {
                                    const percent = (item.count / total) * 100;
                                    const start = currentPercent;
                                    currentPercent += percent;
                                    return `${colors[index % colors.length]} ${start}% ${currentPercent}%`;
                                }).join(', ');
                                return `conic-gradient(${gradient})`;
                            })()
                        }}></div>

                        {/* Lejant (Açıklama) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '200px' }}>
                            {stats.topMedicines.map((med, index) => {
                                const colors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'];
                                const total = stats.topMedicines.reduce((acc, curr) => acc + curr.count, 0);
                                const percent = ((med.count / total) * 100).toFixed(1);
                                return (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: colors[index % colors.length] }}></span>
                                            <span style={{ color: '#5a5c69', fontWeight: '600' }}>{med.name}</span>
                                        </div>
                                        <span style={{ color: '#858796' }}>{percent}% ({med.count})</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <p style={{ color: '#858796', fontStyle: 'italic' }}>Henüz yeterli veri yok.</p>
                )}
            </div>

            {/* Yaklaşan Randevular Başlığı */}
            <div className="header-actions" style={{ marginBottom: '15px' }}>
                <h3 style={{ margin: 0, color: '#4e73df' }}>Yaklaşan Randevular</h3>
                <span 
                    onClick={() => openTab('appointments')} 
                    style={{ textDecoration: 'none', color: '#4e73df', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}>
                    Tümünü Gör &rarr;
                </span>
            </div>

            {/* Özet Tablo */}
            <table className="data-table">
                <thead>
                    <tr>
                        <th>Tarih</th>
                        <th>Kalan Süre</th>
                        <th>Hasta</th>
                        <th>Durum</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.upcomingAppointments.length > 0 ? (
                        stats.upcomingAppointments.map(app => (
                            <tr key={app.id}>
                                <td data-label="Tarih">{new Date(app.appointment_date).toLocaleString()}</td>
                                <td data-label="Kalan Süre">
                                    <span style={{ background: '#e3f2fd', color: '#4e73df', padding: '4px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        {moment(app.appointment_date).fromNow()}
                                    </span>
                                </td>
                                <td data-label="Hasta">{app.patient_name}</td>
                                <td data-label="Durum">
                                    <span className={`status-badge status-${app.status}`}>
                                        {app.status === 'scheduled' ? 'Planlandı' : app.status === 'completed' ? 'Tamamlandı' : 'İptal'}
                                    </span>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="4" style={{ textAlign: 'center' }}>Yaklaşan randevu bulunmuyor.</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default Home;